import React, { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "../ui/dialog";
import { ClockIcon } from "lucide-react";
import { format } from "date-fns";
import { de } from "date-fns/locale";

export function CompletedWorkStepDisplay({ assignment, timeEntries, totalMinutes, onLoad }) {
  const [showDetails, setShowDetails] = useState(false);
  const [loadedTimeEntries, setLoadedTimeEntries] = useState([]);
  const [calculatedTotalMinutes, setCalculatedTotalMinutes] = useState(0);

  const loadCompletedTimeEntries = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/work-step-assignments/${assignment.id}/time-entries`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setLoadedTimeEntries(response.data);
      
      const total = response.data.reduce((sum, entry) => {
        if (entry.start_time && entry.end_time) {
          const start = new Date(entry.start_time);
          const end = new Date(entry.end_time);
          return sum + Math.round((end - start) / (1000 * 60));
        }
        return sum;
      }, 0);
      
      setCalculatedTotalMinutes(total);
    } catch (error) {
      console.error('Failed to load time entries for completed work step:', error);
      setLoadedTimeEntries([]);
      setCalculatedTotalMinutes(0);
    }
  };

  useEffect(() => {
    if (assignment.completed_at && onLoad) {
      loadCompletedTimeEntries();
    }
  }, [assignment.id, assignment.completed_at]);

  const displayTotalMinutes = calculatedTotalMinutes || totalMinutes;
  const displayTimeEntries = loadedTimeEntries.length > 0 ? loadedTimeEntries : timeEntries;

  return (
    <div className="space-y-2">
      <div className="text-sm text-gray-600 bg-gray-50 p-3 rounded">
        <p className="font-medium text-green-800">✅ Arbeitsschritt abgeschlossen</p>
        <p className="text-gray-700">
          <strong>Gesamtzeit:</strong> {displayTotalMinutes} Minuten ({displayTimeEntries.length} Sitzungen)
        </p>
        <p className="text-xs text-gray-500 mt-1">
          Abgeschlossen am: {format(new Date(assignment.completed_at), "PPP HH:mm", { locale: de })}
        </p>
      </div>
      
      <Dialog open={showDetails} onOpenChange={setShowDetails}>
        <DialogTrigger asChild>
          <Button variant="outline" size="sm" className="text-blue-600 hover:text-blue-700">
            <ClockIcon className="h-4 w-4 mr-1" />
            Details anzeigen
          </Button>
        </DialogTrigger>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Arbeitsschritt-Details</DialogTitle>
            <DialogDescription>
              Detaillierte Zeiterfassung für {assignment.work_step?.step_type}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 max-h-96 overflow-y-auto">
            <div className="bg-green-50 p-3 rounded-lg">
              <h4 className="font-medium text-green-900 mb-2">Zusammenfassung</h4>
              <p className="text-sm text-green-700">
                <strong>Gesamtzeit:</strong> {displayTotalMinutes} Minuten
              </p>
              <p className="text-sm text-green-700">
                <strong>Anzahl Sitzungen:</strong> {displayTimeEntries.length}
              </p>
              {assignment.completion_notes && (
                <p className="text-sm text-green-700 mt-2">
                  <strong>Abschluss-Notizen:</strong> {assignment.completion_notes}
                </p>
              )}
            </div>
            
            <div className="space-y-2">
              <h4 className="font-medium">Einzelne Arbeitssitzungen:</h4>
              {displayTimeEntries.map((entry, index) => (
                <div key={entry.id} className="border p-3 rounded-lg bg-white">
                  <div className="flex justify-between items-start mb-2">
                    <span className="font-medium text-sm">Sitzung {index + 1}</span>
                    <Badge variant={entry.entry_type === 'final_completion' ? 'default' : 'secondary'}>
                      {entry.entry_type === 'final_completion' ? 'Final' : 'Pause'}
                    </Badge>
                  </div>
                  <div className="text-sm text-gray-600">
                    <p>
                      <strong>Zeit:</strong> {format(new Date(entry.start_time), "HH:mm")} - {entry.end_time ? format(new Date(entry.end_time), "HH:mm") : 'Laufend'}
                    </p>
                    {entry.end_time && (
                      <p>
                        <strong>Dauer:</strong> {Math.round((new Date(entry.end_time) - new Date(entry.start_time)) / (1000 * 60))} Min
                      </p>
                    )}
                    {entry.actual_quantity && (
                      <p><strong>Stückzahl:</strong> {entry.actual_quantity}</p>
                    )}
                    {entry.work_notes && (
                      <p><strong>Notizen:</strong> {entry.work_notes}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default CompletedWorkStepDisplay;
